// Razorpay Checkout helper — loads the hosted checkout.js script (idempotent),
// creates an order via our backend, opens the modal, and runs verification.
//
// Docs: https://razorpay.com/docs/payments/payment-gateway/web-integration/standard

import axios from 'axios';

const SCRIPT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

let scriptPromise = null;
export const loadRazorpayScript = () => {
  if (typeof window !== 'undefined' && window.Razorpay) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Razorpay script')));
      if (window.Razorpay) resolve();
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Razorpay script'));
    document.body.appendChild(s);
  });
  return scriptPromise;
};

/**
 * Opens the Razorpay Checkout overlay for the given booking. Resolves to the
 * verified booking payload on success; rejects on close/failure.
 *
 *   booking      : the booking object (used for prefill, name, etc.)
 *   purpose      : "booking" (default) or "top_up"
 *   onSuccess    : optional callback fired after server-side verification
 */
export const openRazorpayCheckout = async ({ booking, purpose = 'booking', onSuccess, onDismiss }) => {
  if (!booking || !booking.booking_id) {
    throw new Error('Missing booking');
  }

  await loadRazorpayScript();

  // 1. Create the order on our backend.
  const orderRes = await axios.post(`${API}/payments/razorpay/create-order`, {
    booking_id: booking.booking_id,
    purpose,
  });
  const order = orderRes.data;

  return new Promise((resolve, reject) => {
    const prefillName = booking.customer_name || '';
    const prefillContact = (booking.user_mobile || booking.customer_phone || '').replace(/\D/g, '').slice(-10);

    const options = {
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      order_id: order.order_id,
      name: 'Luggagefreejourney',
      description: purpose === 'top_up'
        ? `Top-up for booking ${booking.baggage_tag || ''}`
        : `Booking ${booking.baggage_tag || ''} — pickup & delivery`,
      // Razorpay shows this image inside the overlay
      image: '/icon-main-192.png',
      prefill: {
        name: prefillName,
        contact: prefillContact ? `+91${prefillContact}` : '',
        email: booking.customer_email || '',
      },
      notes: {
        booking_id: booking.booking_id,
        baggage_tag: booking.baggage_tag || '',
        purpose,
      },
      theme: { color: '#FF4500' },
      modal: {
        ondismiss: () => {
          if (onDismiss) {
            try { onDismiss(); } catch (e) { console.warn('[razorpay] onDismiss callback threw:', e); }
          }
          reject(new Error('Payment cancelled'));
        },
      },
      handler: async (response) => {
        try {
          const verifyRes = await axios.post(`${API}/payments/razorpay/verify`, {
            booking_id: booking.booking_id,
            purpose,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          if (onSuccess) {
            try { onSuccess(verifyRes.data); } catch (e) { console.warn('[razorpay] onSuccess callback threw:', e); }
          }
          resolve(verifyRes.data);
        } catch (e) {
          reject(e);
        }
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', (resp) => {
      const err = resp?.error || {};
      reject(new Error(err.description || 'Payment failed'));
    });
    rzp.open();
  });
};
